import { AllocationConstraintResponse, CreateAllocationConstraintRequest } from '@/app/models/allocationConstraint';
import type { Collaborator } from '@/app/models/collaborator';
import type { Project } from '@/app/models/project';
import {
  useCreateAllocationConstraintMutation,
  useListAllAllocationConstraintByPersonQuery,
} from '@/app/services/allocationConstraintApi';
import { useSearchAllPersonQuery } from '@/app/services/personApi';
import { useSearchProjectsQuery } from '@/app/services/projectApi';
import GenericAutoComplete from '@/components/common/GenericAutoComplete';
import ModalPageTitle from '@/components/common/ModalPageTitle';
import { Box, Stack, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import DrawerButtons from '../common/DrawerButtons';
import TextButton from '../common/TextButton';
import NewEditAllocationConstraintTable from './NewEditAllocationConstraintTable';

interface NewEditAllocationConstraintProps {
  setDrawerOpen: (open: boolean) => void;
  selectedCollaborator: AllocationConstraintResponse | null;
}

export default function NewEditAllocationConstraint(props: NewEditAllocationConstraintProps) {
  const { t } = useTranslation(['allocationConstraint', 'common']);
  const { setDrawerOpen, selectedCollaborator } = props;

  const [selectedProjects, setSelectedProjects] = useState<
    {
      project: Project;
    }[]
  >([]);

  const [project, setProject] = useState<Project | null>(null);
  const [projectSearch, setProjectSearch] = useState('');
  const { data: projects, isFetching: isLoadingProject } = useSearchProjectsQuery({
    searchTerm: projectSearch,
    bringClosed: false,
    page: 0,
    size: 10,
    sortKey: 'name',
    sortDirection: 'ASC',
  });

  const [collaborator, setCollaborator] = useState<Collaborator | null>(selectedCollaborator?.person || null);
  const [collaboratorSearch, setCollaboratorSearch] = useState('');
  const { data: collaboratorsAutoComplete } = useSearchAllPersonQuery(
    {
      searchTerm: collaboratorSearch,
      page: 0,
      size: 10,
      sortKey: 'name',
      sortDirection: 'ASC',
    },
    { skip: collaboratorSearch.length < 1 },
  );

  const { data: foundAllocationConstaintByPerson } = useListAllAllocationConstraintByPersonQuery(
    collaborator?.badge as string,
    {
      skip: !collaborator || !selectedCollaborator,
    },
  );

  useEffect(() => {
    if (foundAllocationConstaintByPerson && foundAllocationConstaintByPerson.length > 0) {
      setSelectedProjects(
        foundAllocationConstaintByPerson.map((item) => ({
          project: item.project as Project,
        })),
      );
    } else {
      setSelectedProjects([]);
    }
  }, [foundAllocationConstaintByPerson]);

  const handleAddCollaborator = () => {
    if (project) {
      setSelectedProjects((prevValue) => [...prevValue, { project }]);
      setProject(null);
      setCollaboratorSearch('');
    }
  };

  const handleRemove = (removedCollaborator: number) => {
    setSelectedProjects((prevValue) => prevValue.filter((col) => col.project.id !== removedCollaborator));
  };

  const [createSpecificAllocation] = useCreateAllocationConstraintMutation();

  const handleSubmit = async () => {
    if (!selectedProjects || selectedProjects.length < 1) return;

    toast.info(t('new.edit.new.value.sent'));

    const requestBody: CreateAllocationConstraintRequest = {
      badge: collaborator?.badge as string,
      codes: selectedProjects.map((p) => p.project.code),
    };

    const response = await createSpecificAllocation(requestBody);

    toast.dismiss();

    if ('data' in response) {
      toast.success(t('new.edit.new.value.added'), { position: toast.POSITION.TOP_RIGHT });
      setDrawerOpen(false);
    } else {
      toast.error(t('common:processing.failed'), { position: toast.POSITION.TOP_RIGHT });
    }
  };

  const handleCancel = () => {
    setDrawerOpen(false);
  };

  const alreadyIncluded = (value: Collaborator) => {
    const filteredCollaborators = selectedProjects.filter((item) => item.project.id === value.id);
    return filteredCollaborators.length >= 1;
  };

  const isFormValid = selectedProjects.length < 1;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 24px)',
        maxHeight: 'calc(100vh - 24px)',
        flexGrow: 'inherit',
      }}
    >
      <Box sx={{ flexGrow: 1 }}>
        <ModalPageTitle
          title={t('new.edit.allocation.restriction')}
          subtitle={t('new.edit.allocation.restriction.subtitle') as string}
        />

        <Box sx={{ mt: 4 }}>
          <Typography variant="fieldHeader" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            {`${t('common:collaborator')}`}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {selectedCollaborator ? (
            <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
              <TextField
                sx={{ flexGrow: 1 }}
                size="small"
                disabled
                value={`${selectedCollaborator.person.badge} - ${selectedCollaborator.person.name}`}
              />
            </Stack>
          ) : (
            <GenericAutoComplete
              value={collaborator}
              onChange={(value) => {
                setCollaborator(value);
              }}
              equalityTest={(option, value) => option?.code === value?.code}
              getOptionLabel={(option) => `${option?.badge} - ${option?.name}` || ''}
              onSearch={(value) => {
                setCollaboratorSearch(value);
              }}
              options={
                collaboratorSearch !== '' && collaboratorsAutoComplete?.content
                  ? collaboratorsAutoComplete?.content
                  : []
              }
              placeholder={t('new.edit.search.collaborator') as string}
              sx={{ width: '80%' }}
            />
          )}
        </Box>

        <Box sx={{ mt: 4 }}>
          <Typography variant="fieldHeader" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            {`${t('common:project')}`}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <GenericAutoComplete
            value={project}
            onChange={(value) => {
              setProject(value);
            }}
            equalityTest={(option, value) => option?.code === value?.code}
            getOptionLabel={(option) =>
              `${option?.code} - ${option?.name}${option?.branchName ? ` (${option?.branchName})` : ''}`
            }
            onSearch={(value) => {
              setProjectSearch(value);
            }}
            options={projectSearch !== '' && projects?.content ? projects?.content : []}
            placeholder={t('new.edit.search.project') || ''}
            loading={isLoadingProject}
            sx={{ width: '80%' }}
          />
          <TextButton
            disabled={(!project && !selectedCollaborator) || !collaborator || alreadyIncluded(collaborator)}
            sx={{ ml: 'auto' }}
            text={`+ ${t('new.edit.restrict')}`}
            onClick={handleAddCollaborator}
          />
        </Box>

        <Box display="flex" alignItems="center" gap={4} sx={{ mt: 2 }} />

        <Box sx={{ mt: 4 }}>
          <NewEditAllocationConstraintTable itens={selectedProjects} onRemove={handleRemove} />
        </Box>
      </Box>

      <Box sx={{ mt: 'auto' }}>
        <DrawerButtons onSave={handleSubmit} onCancel={handleCancel} disabled={isFormValid} />
      </Box>
    </Box>
  );
}
